import { useRouter } from 'next/router'
import { useState, useEffect } from 'react'
import cn from 'classnames'
import { useFormik } from 'formik'
import * as yup from 'yup'

import { Logotype } from 'src/shared/components'
import { Typography } from 'src/shared/ui/Typography'
import { PhoneInput } from 'src/shared/ui/PhoneInput'
import { MaskedInput } from 'src/shared/ui/MaskedInput'
import { Spinner } from 'src/shared/ui/Spinner'
import { Button } from 'src/shared/ui/Button'
import { AccessIcon } from 'src/shared/icons'

import { useRequestSmsCode, useVerifySmsCode } from 'src/shared/api/mutations'
import { cleanPhoneNumber } from 'src/shared/utils/formatPhoneNumber'

import { formatTimer } from './utils'
import styles from './index.module.css'

const SMS_CODE_MASK = [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/]
const SMS_TIMEOUT_SECONDS = 120

type PhoneVerificationStatus = 'idle' | 'pending' | 'success' | 'error' | 'cooldown'
type SmsCodeVerificationStatus = 'idle' | 'error'

const validationSchema = yup.object({
  phone: yup.string().required('Некорректный номер телефона').length(18, 'Некорректный номер телефона'),
  smsCode: yup.string().when('isSmsCodeVisible', {
    is: true,
    then: yup.string().required('Введите код из смс').length(4, 'Некорректный код'),
    otherwise: yup.string().notRequired(),
  }),
})

export const Authorization = () => {
  const router = useRouter()

  const { mutateAsync: requestSmsCode } = useRequestSmsCode()
  const { mutateAsync: checkSmsCode } = useVerifySmsCode()

  const [verificationStatus, setVerificationStatus] = useState<PhoneVerificationStatus>('idle')
  const [verificationSmsCodeStatus, setVerificationSmsCodeStatus] = useState<SmsCodeVerificationStatus>('idle')
  const [cooldownTimer, setCooldownTimer] = useState<number>(SMS_TIMEOUT_SECONDS)
  const [isSmsCodeVisible, setIsSmsCodeVisible] = useState<boolean>(false)

  const formik = useFormik({
    initialValues: {
      phone: '',
      smsCode: '',
      isSmsCodeVisible,
    },
    validationSchema,
    onSubmit: async () => {
      if (isSmsCodeVisible) {
        await handleSubmitWithSmsCode()
      } else {
        await handleRequestSmsCode()
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  })

  const isCooldownActive = verificationStatus === 'cooldown' && cooldownTimer > 0
  const canRequestNewCode = verificationStatus === 'cooldown' && cooldownTimer <= 0

  useEffect(() => {
    formik.setFieldValue('isSmsCodeVisible', isSmsCodeVisible)
  }, [isSmsCodeVisible])

  useEffect(() => {
    if (!isCooldownActive) return

    const interval = setInterval(() => {
      setCooldownTimer((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [isCooldownActive])

  useEffect(() => {
    if (verificationStatus !== 'success') return

    const timeout = setTimeout(() => {
      setVerificationStatus('cooldown')
      setCooldownTimer(SMS_TIMEOUT_SECONDS)
    }, 5000)

    return () => clearTimeout(timeout)
  }, [verificationStatus])

  const handleRequestSmsCode = async () => {
    await formik.validateField('phone')
    if (formik.errors.phone) return

    setVerificationStatus('pending')

    try {
      await requestSmsCode({
        phone: cleanPhoneNumber(formik.values.phone),
      })
      setVerificationStatus('success')
      setIsSmsCodeVisible(true)
    } catch (error) {
      setVerificationStatus('error')
    }
  }

  const handleResetSmsCode = () => {
    if (!canRequestNewCode) return

    setVerificationStatus('cooldown')
    setCooldownTimer(SMS_TIMEOUT_SECONDS)

    handleRequestSmsCode()
  }

  const handleSubmitWithSmsCode = async () => {
    try {
      await checkSmsCode({ phone: cleanPhoneNumber(formik.values.phone), code: formik.values.smsCode })
      router.push('/profile')
    } catch (error) {
      setVerificationSmsCodeStatus('error')
    }
  }

  const renderPhoneStatusMessage = () => {
    switch (verificationStatus) {
      case 'success':
        return (
          <div className={styles.accessMessage}>
            <Typography view="reg_10" color="var(--color-green)">
              Cмс отправлено
            </Typography>
            <AccessIcon className={styles.accessIcon} />
          </div>
        )
      case 'error':
        return (
          <Typography view="reg_10" color="var(--color-red)">
            Ошибка отправки смс
          </Typography>
        )
      case 'pending':
        return <Spinner color="var(--color-gray)" size={12} />
      case 'cooldown':
        return (
          <Typography
            view="reg_10"
            className={cn(styles.timer, { [styles.disabled]: !canRequestNewCode })}
            onClick={handleResetSmsCode}
          >
            {canRequestNewCode ? 'Получить новый код' : `Повторная отправка через ${formatTimer(cooldownTimer)}`}
          </Typography>
        )
      default:
        return null
    }
  }

  const renderSmsCodeStatusMessage = () => {
    switch (verificationSmsCodeStatus) {
      case 'error': {
        return (
          <Typography view="reg_10" color="var(--color-red)">
            Неверный код
          </Typography>
        )
      }
      default:
        return null
    }
  }

  const renderPhoneError = () => {
    if (formik.touched.phone && formik.errors.phone) {
      return (
        <Typography view="reg_10" color="var(--color-red)">
          {formik.errors.phone}
        </Typography>
      )
    }

    return renderPhoneStatusMessage()
  }

  const renderSmsCodeError = () => {
    if (formik.touched.smsCode && formik.errors.smsCode) {
      return (
        <Typography view="reg_10" color="var(--color-red)">
          {formik.errors.smsCode}
        </Typography>
      )
    }

    return renderSmsCodeStatusMessage()
  }

  const isSubmitDisabled =
    verificationStatus === 'pending' || (isSmsCodeVisible ? !formik.isValid : !!formik.errors.phone)

  return (
    <div className={styles.authPage}>
      <div className={styles.authContainer}>
        <Logotype className={styles.logo} />

        <form className={styles.authForm} onSubmit={formik.handleSubmit}>
          <Typography view="semibold_20">Войдите по номеру телефона</Typography>

          <div className={styles.inputFields}>
            <PhoneInput
              fieldClassName={cn({
                [styles.verified]: verificationStatus === 'success',
              })}
              label="Номер телефона"
              placeholder="+7 (999) 123-45-67"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && !!formik.errors.phone}
              rightAddons={renderPhoneError()}
            />

            {isSmsCodeVisible && (
              <MaskedInput
                label="Код из SMS"
                mask={SMS_CODE_MASK}
                placeholder="_ _ _ _"
                name="smsCode"
                value={formik.values.smsCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.smsCode && !!formik.errors.smsCode}
                rightAddons={renderSmsCodeError()}
              />
            )}
          </div>

          <Button size="lg" type="submit" disabled={isSubmitDisabled}>
            {isSmsCodeVisible ? 'Войти' : 'Получить код по SMS'}
          </Button>
        </form>

        <Typography view="reg_10" color="var(--color-gray)" className={styles.agreementContainer}>
          Продолжая пользоваться сервисом, вы принимаете <br />
          <Typography
            view="reg_10"
            tag="span"
            className={styles.agreement}
            onClick={() => router.push('/privacy-policy')}
          >
            Политику конфиденциальности
          </Typography>
        </Typography>
      </div>
    </div>
  )
}
